import { enumParam, trimmedStringParam } from "../../lib/url-state";

/** `building` is a world the real-to-sim engineer is still building (a draft); the rest are
 *  published worlds by kind. One list holds both. */
export type WorldFilter =
  | "all"
  | "building"
  | "captured"
  | "generated"
  | "object"
  | "scene";

export const WORLD_FILTERS: readonly WorldFilter[] = [
  "all",
  "building",
  "captured",
  "generated",
  "object",
  "scene",
];

/** What the shared world-asset browser has selected.
 *
 * `world` is the asset being inspected and is the ADDRESS
 * (`…/build-worlds/assets/$worldId` or `…/simulate/assets/$worldId`); the address says
 * `assets` and not `worlds` because no human-facing address spells a platform pillar
 * (`docs/BRAND.md` §11). The rest is filter and view state, which is why it stays in the
 * query, `operation` among it (see {@link WorldsFilters.operation}). */
export interface WorldsSearch extends WorldView {
  readonly world?: string;
}

/** The filters every simulate address carries. */
export interface WorldsFilters {
  readonly kind?: Exclude<WorldFilter, "all">;
  readonly q?: string;
  /** An authoring job's receipt. It is transient, not identity, so it stays in the query:
   *  it names a job the page is watching, not a thing the page shows. While the job runs
   *  the browser reports its progress over the same collection; when an environment job
   *  succeeds the page selects the environment it made, whose id is the path. A reload
   *  keeps watching. The Simulation collection watches it through reload and
   *  retains it when opening a legacy authoring bookmark. */
  readonly operation?: string;
}

/** One asset's address adds how it is being looked at. `explore` is a way of reading a
 *  world, not a different world, so it stays a query key beside the path. */
export interface WorldView extends WorldsFilters {
  readonly view?: "explore";
}

export function parseWorldsFilters(
  search: Record<string, unknown>,
): WorldsFilters {
  return {
    kind: enumParam(search.kind, ["building", "captured", "generated", "object", "scene"]),
    q: trimmedStringParam(search.q),
    operation: trimmedStringParam(search.operation),
  };
}

export function parseWorldView(search: Record<string, unknown>): WorldView {
  return {
    ...parseWorldsFilters(search),
    view: enumParam(search.view, ["explore"]),
  };
}
